import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Users, BookOpen, ShieldOff } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator } from '../../../mascot';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import type { Classroom } from '../../../accounts';
interface CoTeacherWelcomeModalProps {
  open: boolean;
  classroom: Classroom | null;
  onClose: () => void;
}
/**
 * One-time welcome shown after a teacher accepts a co-teacher invite.
 *
 * Djay-zone: YES (Zone 1 — first-run / celebration).
 * Wave 3.3 — Co-teacher accept flow.
 */
export function CoTeacherWelcomeModal({
  open,
  classroom,
  onClose
}: CoTeacherWelcomeModalProps) {
  useEscapeKey(onClose, open && classroom !== null);
  return <AnimatePresence data-id="element-9045" data-mp="1kfuqm0z3w53w">
      {open && classroom && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="co-teacher-welcome-title" data-id="element-9046" data-mp="1kejlnxtxcrb7">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-9047" data-mp="2qzpa48vjs4an" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-9048" data-mp="3aq2f5qcywjmk">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-indigo-400 to-amber-400" data-id="element-9049" data-mp="1ucudxz30n7tt" />

            <div className="p-8 text-center" data-id="element-9050" data-mp="1ab99kz87xj61">
              <div className="flex justify-center mb-4" data-id="element-9051" data-mp="168w6yjorq1dh">
                <div className="relative" data-id="element-9052" data-mp="11h4n0s04atwb">
                  <DjayNarrator size="md" emotion="celebrating" data-id="element-9053" data-mp="11g32zy14ix2g" />
                  <Sparkles className="absolute -top-1 -right-2 w-5 h-5 text-amber-400" data-id="element-9054" data-mp="wi9tav0m3kxf" />
                </div>
              </div>

              <h2 id="co-teacher-welcome-title" className="text-2xl font-black text-slate-900 mb-2" data-id="element-9055" data-mp="rk02sxpu18i5">
                Welkom in {classroom.name}!
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-6" data-id="element-9056" data-mp="iise97d4bm19">
                Je bent toegevoegd als{' '}
                <span className="font-bold" data-id="element-9057" data-mp="dl8opbayzmu0">co-docent</span>. Hieronder zie je
                wat je kunt doen.
              </p>

              <ul className="text-left space-y-3 mb-6" data-id="element-9058" data-mp="27xgde6wp77hc">
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-9059" data-mp="27xq8ua0ii6uf">
                  <Users className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-9060" data-mp="2fgfz4j7mtb3v" />
                  <div data-id="element-9061" data-mp="2ke7y1x6p1xhk">
                    <p className="text-sm font-bold text-slate-900" data-id="element-9062" data-mp="2kdpwqm56ct2s">
                      Leerlingen begeleiden
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-9063" data-mp="2u63wxagltrmu">
                      Je ziet alle leerlingen, hun voortgang en kunt leerlingen
                      toevoegen of verwijderen.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-9064" data-mp="21j132vmvi52x">
                  <BookOpen className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-9065" data-mp="26ie0297gksj8" />
                  <div data-id="element-9066" data-mp="2aixu33wqjg15">
                    <p className="text-sm font-bold text-slate-900" data-id="element-9067" data-mp="3hv6oba11iyuj">
                      Huiswerk &amp; toetsen
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-9068" data-mp="3lvqnej56ccjb">
                      Je kunt zelfstandig huiswerk en toetsen klaarzetten voor
                      deze klas.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-9069" data-mp="3fimex258i7td">
                  <ShieldOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9070" data-mp="3ubfu0mglglxi" />
                  <div data-id="element-9071" data-mp="3pgsehd3kj1v2">
                    <p className="text-sm font-bold text-amber-900" data-id="element-9072" data-mp="792xxhnlbq75">
                      Klas-structuur blijft bij eigenaar
                    </p>
                    <p className="text-xs text-amber-800" data-id="element-9073" data-mp="29887hfjx6no">
                      Alleen de klas-eigenaar kan de klas hernoemen, verwijderen
                      of andere co-docenten beheren.
                    </p>
                  </div>
                </li>
              </ul>

              <Button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl text-sm font-bold transition-all" data-id="element-9074" data-mp="pysuoaawym1p">
                Aan de slag
              </Button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}